// Implements: F-UIX-014, F-UIX-009, F-UIX-020, F-UIX-018, F-ITK-007, F-IDN-012, F-IDN-027
/**
 * The route registry: four frozen routes, one guard, one catch-all.
 *
 * `/login`, `/`, `/mockups/:mockupId` and
 * `/mockups/:mockupId/versions/:versionId` are the whole surface of this
 * change (DD23). Each is reachable by URL, shareable, and part of the history:
 * back and forward return the previous view without a full reload, because
 * `createBrowserRouter` navigates in the client.
 *
 * The guard is a COMPOSITION component and not a `loader` (DD23). A loader
 * would ask the API for the session on every navigation, blind to the TanStack
 * cache, so the session would have two sources of truth and signing out would
 * have to invalidate both. `RequireSession` reads `useSession()` — the same
 * query everything else reads — and renders `<Outlet/>`, a `<Navigate>` or the
 * loading state.
 *
 * `UnauthorizedRedirect` is the other half of design DD6: `httpClient` knows
 * how to detect a dead session but knows neither the router nor the cache, so
 * the composition root hands it a function that clears one and drives the
 * other. It is installed inside the router because the navigation has to be a
 * client navigation, and it is removed on unmount so a test never inherits the
 * handler of the previous one.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { ReactElement } from "react";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import type { RouteObject } from "react-router";

import { setUnauthorizedHandler } from "../shared/api/httpClient";
import { LOGIN_ROUTE, loginPathWithNext, readNextParam } from "../shared/api/nextParam";
import { emptySession, useSession } from "../shared/api/session";
import { useTranslation } from "../shared/i18n";
import { ErrorState } from "../shared/ui/molecules/ErrorState";
import { LoadingState } from "../shared/ui/molecules/LoadingState";
import { backofficeRoute } from "./backoffice";
import { PLATFORM_ADMIN_PATH, RequirePlatformAdmin } from "./guards/RequirePlatformAdmin";
import { AppShell } from "./layout/AppShell";
import { ACCOUNT_PATH } from "./layout/accountTabs";
import { PLATFORM_PATHS } from "./layout/navigation";
import { AccountPage } from "./pages/AccountPage";
import { DesignProfilesPage } from "./pages/DesignProfilesPage";
import { GalleryPage } from "./pages/GalleryPage";
import { LienzoPage } from "./pages/LienzoPage";
import { LoginPage } from "./pages/LoginPage";
import { MockupPage } from "./pages/MockupPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import {
  PlatformAdminRedirect,
  PlatformCeilingsPage,
  PlatformModelsPage,
  PlatformStatusPage,
  PlatformTotalsPage,
} from "./pages/PlatformAdminPage";
import { InvitationPage } from "./pages/InvitationPage";
import { ForgotPasswordPage, ResetPasswordPage, ResetSettledPage } from "./pages/RecoveryPages";
import { PlatformOrganizationsPage } from "./pages/PlatformOrganizationsPage";
import { SharingPage } from "./pages/SharingPage";
import { VersionPage } from "./pages/VersionPage";
import { WhatsAppPage } from "./pages/WhatsAppPage";

/**
 * The four frozen paths of F-UIX-014, written once, plus the design profiles
 * screen (F-UIX-020) and the links panel (F-SHR-009) of phase 6, which sit
 * behind the same guard. The backoffice of phase 7 (F-UIX-018) is a route group
 * under `/backoffice` declared in `./backoffice`, behind the same guard, and the
 * platform admin's view (F-UIX-019, phase 7) sits behind its own role guard as well. The
 * WhatsApp linking section (UIX.4, phase 8) answers as the not-found page while intake is disabled.
 * The redesign (UX-04) moves the mockup to the Lienzo: `/lienzo` creates, `/lienzo/:mockupId` edits,
 * and `/mockups/:mockupId` stays a valid address that redirects there; the version pages keep theirs.
 */
export const ROUTE_PATHS = {
  login: LOGIN_ROUTE,
  gallery: "/",
  lienzo: "/lienzo",
  editor: "/lienzo/:mockupId",
  mockup: "/mockups/:mockupId",
  version: "/mockups/:mockupId/versions/:versionId",
  designProfiles: "/design/profiles",
  sharing: "/sharing",
  whatsapp: "/whatsapp",
  sessions: "/sesiones",
  // Mockup v3 P1: the own account; `/sesiones` now only redirects to its sessions tab.
  account: ACCOUNT_PATH,
  accountTab: `${ACCOUNT_PATH}/:tab`,
  // Mockup v3 P3: the address an invitation link opens (Default P3-1: P3 builds its page).
  invitation: "/invitacion/:token",
  // P2: the public password recovery pages (v3 `#/recuperar`, `#/restablecer/<token>`).
  recovery: "/recuperar",
  reset: "/restablecer/:token",
  // Where a link that ended lands: its token replaced out of the address and the history.
  resetSettled: "/restablecer",
  platformAdmin: PLATFORM_ADMIN_PATH,
  platform: "/plataforma",
  platformOrganizations: PLATFORM_PATHS.organizations,
  platformTotals: PLATFORM_PATHS.totals,
  platformCeilings: PLATFORM_PATHS.ceilings,
  platformModels: PLATFORM_PATHS.models,
  platformStatus: PLATFORM_PATHS.status,
} as const;

/**
 * Installs the `401` handler for as long as the application is mounted.
 *
 * The cache is cleared before the navigation, not after: a route that renders
 * with the previous person's cached answers, even for one frame, has already
 * shown them.
 */
export function UnauthorizedRedirect(): ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  // One redirect per expiry, and the reason is not tidiness. Clearing the
  // cache makes every mounted query refetch, and on a session that is gone
  // each of those answers `401` too: without this guard the first expiry
  // clears and navigates once per mounted query, in a loop that never lets the
  // router render the login screen at all.
  const redirecting = useRef(false);

  // A new location means the redirect landed, so the next `401` is a new
  // event and not an echo of this one.
  useEffect(() => {
    redirecting.current = false;
  }, [location.key]);

  useEffect(() => {
    const previous = setUnauthorizedHandler((redirectTo) => {
      if (redirecting.current) return;
      redirecting.current = true;
      emptySession(queryClient);
      void navigate(redirectTo, { replace: true });
    });
    return () => {
      setUnauthorizedHandler(previous);
    };
  }, [queryClient, navigate]);

  return <Outlet />;
}

/**
 * Every route but `/login` demands a session.
 *
 * An anonymous visitor is sent to `/login?next=…` with the path plus query
 * they were trying to reach, validated by the same function the interceptor
 * uses. While the bootstrap is still in flight nothing is decided: redirecting
 * during `loading` would send a signed-in person to the login screen on every
 * reload. An `unavailable` bootstrap decides nothing either: the outage is
 * reported where the visitor already is, with a retry, because redirecting
 * would turn a backend that is down into a sign-out that loses the address.
 */
export function RequireSession(): ReactElement {
  const { state, retry } = useSession();
  const { t } = useTranslation();
  const location = useLocation();

  if (state.status === "loading") return <LoadingState label={t("common.state.loading")} />;
  if (state.status === "unavailable") return <ErrorState error={state.error} onRetry={retry} />;
  if (state.status === "anonymous") {
    return <Navigate to={loginPathWithNext(`${location.pathname}${location.search}`)} replace />;
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/**
 * `/login`, which is the one route a session must NOT stay on.
 *
 * Somebody who already has a session and opens `/login` goes to their `next`,
 * or to `/` when there is none or it does not validate. The same validation
 * runs when the value is written into the URL and when it is read back out of
 * it: between those two moments it lives in the address bar, where nothing
 * this application controls protects it.
 */
export function LoginRoute(): ReactElement {
  const { state } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const target = readNextParam(location.search);

  if (state.status === "authenticated") return <Navigate to={target} replace />;
  return (
    <LoginPage
      onSuccess={() => {
        void navigate(target, { replace: true });
      }}
    />
  );
}

/**
 * `/mockups/:mockupId`, the mockup's address before the Lienzo (UX-04): a bookmark, a shared
 * address or an older link lands on `/lienzo/:mockupId`. The query, the hash and the history
 * state travel with it, because the state may carry the first-turn hand-off of a creation.
 */
export function LegacyMockupRedirect(): ReactElement {
  const { mockupId = "" } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: `/lienzo/${encodeURIComponent(mockupId)}`, search: location.search, hash: location.hash }}
      state={location.state as unknown}
      replace
    />
  );
}

/** The route table, exported so a memory router can mount exactly this tree. */
export const ROUTES: readonly RouteObject[] = [
  // Mockup v3 P3 (Default P3-1): public, and outside the 401 redirect, because an unknown or
  // rotated token answers 401 and that is said on the page itself, not sent to /login.
  { path: ROUTE_PATHS.invitation, element: <InvitationPage /> },
  // P2: public as well; the confirmation's 401 is a dead link, said on its page, not a lost session.
  { path: ROUTE_PATHS.recovery, element: <ForgotPasswordPage /> },
  { path: ROUTE_PATHS.reset, element: <ResetPasswordPage /> },
  { path: ROUTE_PATHS.resetSettled, element: <ResetSettledPage /> },
  {
    element: <UnauthorizedRedirect />,
    children: [
      { path: ROUTE_PATHS.login, element: <LoginRoute /> },
      {
        element: <RequireSession />,
        children: [
          { path: ROUTE_PATHS.gallery, element: <GalleryPage /> },
          { path: ROUTE_PATHS.lienzo, element: <LienzoPage /> },
          { path: ROUTE_PATHS.editor, element: <MockupPage /> },
          { path: ROUTE_PATHS.mockup, element: <LegacyMockupRedirect /> },
          { path: ROUTE_PATHS.version, element: <VersionPage /> },
          { path: ROUTE_PATHS.designProfiles, element: <DesignProfilesPage /> },
          { path: ROUTE_PATHS.sharing, element: <SharingPage /> },
          { path: ROUTE_PATHS.whatsapp, element: <WhatsAppPage /> },
          { path: ROUTE_PATHS.sessions, element: <Navigate to={`${ACCOUNT_PATH}/sesiones`} replace /> },
          { path: ROUTE_PATHS.account, element: <Navigate to={`${ACCOUNT_PATH}/perfil`} replace /> },
          { path: ROUTE_PATHS.accountTab, element: <AccountPage /> },
          // The backoffice (F-UIX-018): one route group with its own layout and sections.
          backofficeRoute(),
          // The platform admin's view (UIX.8): any other role gets the not-found page. Since UX-11
          // it is four pages, each behind its own action; `/admin` and `/plataforma` only redirect.
          {
            element: <RequirePlatformAdmin />,
            children: [
              { path: ROUTE_PATHS.platformAdmin, element: <PlatformAdminRedirect /> },
              { path: ROUTE_PATHS.platform, element: <PlatformAdminRedirect /> },
              { path: ROUTE_PATHS.platformOrganizations, element: <PlatformOrganizationsPage /> },
              { path: ROUTE_PATHS.platformTotals, element: <PlatformTotalsPage /> },
              { path: ROUTE_PATHS.platformCeilings, element: <PlatformCeilingsPage /> },
              { path: ROUTE_PATHS.platformModels, element: <PlatformModelsPage /> },
              { path: ROUTE_PATHS.platformStatus, element: <PlatformStatusPage /> },
            ],
          },
          // The catch-all is INSIDE the guard: an unknown address is still an
          // address that is not `/login`, and the capability asks every one of
          // those for a session.
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

/**
 * The one browser router, built on first use.
 *
 * Lazily, because `createBrowserRouter` subscribes to `history` the moment it
 * is called, and importing this module to read `ROUTES` — which every router
 * test does — should not start listening to a history the test is about to
 * replace with its own.
 */
let browserRouter: ReturnType<typeof createBrowserRouter> | null = null;

function appRouter(): ReturnType<typeof createBrowserRouter> {
  browserRouter ??= createBrowserRouter([...ROUTES]);
  return browserRouter;
}

/** The sixth provider of design section 4, mounted by `src/main.tsx`. */
export function AppRouter(): ReactElement {
  return <RouterProvider router={appRouter()} />;
}
